import { INSTANCES_NAV, GOUVERNANCE_VIEWS, sectionsPour, routeInstance } from "./instancesNav";
import { C } from "./couleurs";
import { accessScopeForView } from "./accesVues";
// =============================================================================
// navConstants.js — Structure du menu de navigation, extraite d'App.jsx
// (Révision S1 tranche 3). Déplacement pur, seuls les préfixes `export` ont
// été ajoutés. Consommée par le Header (src/Header.js : barre desktop +
// drawer mobile) ET par App.jsx (menu de répartition de la page d'accueil,
// vues plan du site). NAV_COLORS est la table UNIQUE des teintes de module
// (lot L3 « Identité en un fichier », 27/09/2026) : ne la recopiez nulle part.
// =============================================================================

// ── NAVIGATION : source unique partagée (menu Header + page d'accueil) ───────
// La structure du menu est définie UNE fois ici (module-level) et consommée à
// la fois par le Header (barre + drawer + dropdowns) et par le « menu de
// répartition » de la page d'accueil. Évite toute duplication/désync.
// ── IDENTITÉ DES MODULES (lot L3, refonte « Mosaïque vivante », 27/09/2026) ──
// Teintes de la charte Enio (décision de Pierre du 27/09 : Gouvernance violet
// #951B81, Marchés bleu, Entreprises sable, Événements magenta, Conformité
// rouge ; Activité, Nos Mutuelles, RH et Pilotage inchangés). Header (boutons,
// panneaux), rail, tiroir, feuille « Modules », barre d'onglets, bannières
// (MODULE_COLORS d'uxComponents, DÉRIVÉ) et html[data-module] (theme.css,
// miroir vérifié par src/identiteModules.test.js) en dérivent.
// UNE exception écrite : Affaires publiques indigo #4338CA, hors charte,
// décision de Pierre du 12/10/2026 (le module sort d'Activité : « un module
// assez solide pour être identifié à part »). Toutes les autres teintes de
// module restent celles de la charte (src/identiteModules.test.js).
export const NAV_COLORS = { athos:"#1D948A", ap:"#4338CA", gouv:"#951B81", mutuelles:"#F49800", exploration:"#2AA9E1", events:"#D60B52", rh:"#94C11F", hygie:"#E20816", pilotage:"#003F59", argos:"#CBBB9F" };
// Teinte NEUTRE d'« Administration » et « Super administration » (rail, 29/09/2026) :
// l'ardoise de la charte (--enio-slate du DS). Hors NAV_COLORS : ce ne sont pas
// des modules métier (voir buildNavAdmin). Encre de texte --tx-ardoise (theme.css).
export const TEINTE_ADMIN = "#5C6B73";

// Icône de chaque module (clé de src/icones.js) : tuile de la feuille
// « Modules », onglet contextuel de la barre basse, en-tête de panneau, bannière.
export const NAV_ICONES = { athos:"activity", ap:"speakerphone", gouv:"gavel", mutuelles:"heart-handshake", exploration:"file-search", events:"calendar-event", rh:"users-group", hygie:"shield-check", pilotage:"steering-wheel", argos:"buildings" };
// Libellé de chaque module (lot L4) : lu par buildNavModules ET par le fil
// « Enio / Module / Page » de l'en-tête de page (EnTetePage.js).
// Dans l'ORDRE DU RAIL (src/usagesModules.js en tire la liste des modules).
export const NAV_LIBELLES = { athos:"Activité", ap:"Affaires publiques", gouv:"Gouvernance", mutuelles:"Nos Mutuelles", argos:"Entreprises", exploration:"Marchés", events:"Événements", rh:"Ressources humaines", hygie:"Conformité", pilotage:"Pilotage" };
// Anciennes clés de module encore passées à <ModuleHero module="…"> : ALIAS
// d'un module de navigation (MODULE_COLORS d'uxComponents, PlaqueModule,
// EnTetePage). Une clé inconnue retombe sur Pilotage (navy).
export const ALIAS_MODULES = { agora:"gouv", themis:"gouv", consultation:"gouv", veille:"exploration", dataviz:"mutuelles", gelppe:"hygie", admin:"pilotage" };
export const cleModule = (m) => (Object.prototype.hasOwnProperty.call(NAV_COLORS, m) ? m : ALIAS_MODULES[m] || "pilotage");

// Encres fixes : blanc, et navy profond de la charte (glyphe sur les teintes
// claires). Les tables ci-dessous sont indexées PAR NAV_COLORS (clés calculées)
// pour ne recopier aucune teinte : cliquet des hex de L2 (src/cliquet.test.js).
const BLANC = "#FFFFFF", NAVY_PROFOND = "#002C3F";
const T = NAV_COLORS;

// Couleur d'une teinte quand elle sert de TEXTE : jeton theme-aware --tx-*
// (theme.css). La teinte brute ne sert jamais de texte : orange, sable, vert et
// bleu sont illisibles sur fond clair ; navy et violet sur fond sombre. Les
// anciennes teintes hors charte restent reconnues (encore présentes hors de la
// navigation jusqu'au codemod du lot L10). Clés en MAJUSCULES (lookup insensible
// à la casse ; les valeurs de NAV_COLORS le sont déjà).
const NAV_TX = {
  [T.pilotage]: "var(--tx-navy)",     [T.athos]: "var(--tx-teal)",   [T.gouv]: "var(--tx-violet)",
  [T.mutuelles]: "var(--tx-orange)",  [T.exploration]: "var(--tx-blue)", [T.argos]: "var(--tx-sable)",
  [T.events]: "var(--tx-magenta)",    [T.hygie]: "var(--tx-red)",    [T.rh]: "var(--tx-green)",
  // Affaires publiques (12/10/2026) : l'ancienne teinte hors charte #4338CA,
  // devenue celle du module ; son encre --tx-indigo (theme.css) vaut dans les
  // deux thèmes.
  [T.ap]: "var(--tx-indigo)",
  // hors charte (retirées de la navigation au lot L3) et pseudo-sections Admin :
  "#185FA5": "var(--tx-blue)",   "#7C4DBB": "var(--tx-violet)",  "#0891B2": "var(--tx-blue)",
  "#C2185B": "var(--tx-magenta)", "#14506E": "var(--tx-navy)",
  "#D4AF37": "var(--tx-gold)",
  [TEINTE_ADMIN]: "var(--tx-ardoise)",
  // teintes d'origine des palettes catégorielles laissées hors charte (deux
  // teintes s'y seraient confondues : donuts d'engagement, statuts de note de
  // frais, sous-traitants RGPD…) : leur TEXTE prend l'encre de sa famille.
  ...Object.fromEntries(Object.entries({
    teal: "#14B8A6 #0D9488", green: "#059669 #22C55E #10B981 #16A34A", blue: "#3B82F6 #0EA5E9 #1E40AF",
    orange: "#F59E0B #EA580C #D97706", violet: "#A855F7 #7C3AED #8B5CF6", indigo: "#6366F1",
    red: "#EF4444 #DC2626 #E11D48", magenta: "#EC4899",
  }).flatMap(([k, hs]) => hs.split(" ").map((h) => [h, `var(--tx-${k})`]))),
  // gris de la charte (relecture L10b) : #8a9aaa en texte = 2,9:1 sur blanc
  [C.gray.toUpperCase()]: "var(--text-sub)",
};
const enMaj = (c) => String(c || "").trim().toUpperCase();
export const navTexte = (c) => NAV_TX[enMaj(c)] || c;

// Glyphe (ou texte) sur une TUILE PLEINE de la teinte — synthèse §4.1 : blanc,
// ou navy profond #002C3F là où le blanc échoue. Jamais de blanc sur orange
// (2,25:1), sable (1,88:1), vert (2,12:1) ni bleu (2,67:1).
const ENCRE_SUR = {
  [T.athos]: BLANC, [T.ap]: BLANC, [T.gouv]: BLANC, [T.events]: BLANC, [T.hygie]: BLANC, [T.pilotage]: BLANC,
  [T.mutuelles]: NAVY_PROFOND, [T.exploration]: NAVY_PROFOND, [T.argos]: NAVY_PROFOND, [T.rh]: NAVY_PROFOND,
  [TEINTE_ADMIN]: BLANC,
};
// Encre FIXE de la teinte sur fond blanc (bouton blanc d'une bannière, carte
// toujours claire) — valeurs AA de la synthèse §4.1.
const ENCRE_CLAIRE = {
  [T.athos]: "#0F6B62", [T.ap]: T.ap, [T.gouv]: T.gouv, [T.mutuelles]: "#9A5E00", [T.exploration]: "#11688F",
  [T.argos]: "#6E5F43", [T.events]: "#A30840", [T.hygie]: "#A6060F", [T.rh]: "#58750C", [T.pilotage]: T.pilotage,
  [TEINTE_ADMIN]: TEINTE_ADMIN,
  [C.gray.toUpperCase()]: "var(--acc-gray-ink)",
};
// Encre du THÈME → encre fixe (relecture L10b) : sur un fond blanc codé en dur,
// var(--tx-navy) deviendrait #7FB3D5 en thème sombre (2,26:1). Le texte
// principal aussi (tour 3) : var(--text-main) vaut #FFFFFF en thème sombre, soit
// du blanc sur blanc ; il devient le navy profond de la charte (#002C3F, au plus
// près de #00293A, sa valeur claire). Le texte atténué prend le gris fixe.
const FIXE_DU_THEME = { navy: "var(--acc-navy)", violet: "var(--acc-violet)", gold: "var(--acc-orange-ink)", indigo: "var(--acc-indigo)" };
const FIXE_DU_TEXTE = { main: "var(--acc-navy-deep)", sub: "var(--acc-gray-ink)", muted: "var(--acc-gray-ink)" };
const fixeDuTheme = (c) => {
  const m = /^var\(--(tx|text)-(\w+)\)$/i.exec(String(c || "").trim());
  if (!m) return c;
  if (m[1].toLowerCase() === "text") return FIXE_DU_TEXTE[m[2].toLowerCase()] || c;
  const k = m[2].toLowerCase();
  return FIXE_DU_THEME[k] || (/^(teal|green|red|blue|magenta|orange|sable)$/.test(k) ? `var(--acc-${k}-ink)` : c);
};

/** Contraste WCAG 2.x entre deux couleurs #RRGGBB. */
export function contrasteWcag(a, b) {
  const lum = (h) => {
    const x = enMaj(h).replace("#", "");
    return [0, 2, 4].map((i) => parseInt(x.slice(i, i + 2), 16) / 255)
      .map((v) => (v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)))
      .reduce((s, v, i) => s + v * [0.2126, 0.7152, 0.0722][i], 0);
  };
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
}
const estHex = (c) => /^#[0-9A-F]{6}$/.test(enMaj(c));
export function encreSur(c) {
  const k = enMaj(c);
  if (ENCRE_SUR[k]) return ENCRE_SUR[k];
  if (!estHex(k)) return BLANC;
  return contrasteWcag(BLANC, k) >= contrasteWcag(NAVY_PROFOND, k) ? BLANC : NAVY_PROFOND;
}
export const encreSurBlanc = (c) => ENCRE_CLAIRE[enMaj(c)] || fixeDuTheme(navTexte(c));
/** Fond d'une bannière à texte BLANC : la teinte si le blanc y tient (≥ 3:1,
 *  titre en grand corps), sinon son encre foncée (orange, bleu, sable, vert). */
export const fondBanniere = (c) => (estHex(c) && contrasteWcag(BLANC, c) >= 3 ? c : encreSurBlanc(c));

/** Teinte de la TUILE d'une entrée dans une liste de menu (panneau survolé,
 *  tiroir, feuille « Modules », « Tout Aramis », vitrine) : celle du MODULE,
 *  sauf pour une instance de Gouvernance (`instance`). Les quatre instances
 *  portent le même losange : seule leur sous-teinte les distingue (synthèse
 *  §4.5). Sans cette exception, AG, CA, CODIR et Bureau s'affichaient en
 *  quatre losanges violets identiques (relecture du lot L3). Le rail lit déjà
 *  `it.color`. Verrou : src/menuTuiles.test.js. */
export const teinteEntree = (it, couleurModule) => (it && it.instance && it.color) || couleurModule;

// Vues du module Argos. Déclarées ici plutôt que dupliquées dans App.jsx et
// Header.js : App.jsx s'en sert pour son point de montage UNIQUE (le module
// route en interne, ce qui préserve son cache de recherche d'une vue à
// l'autre), Header.js pour surligner l'onglet actif. Ce fichier est déjà dans
// le bundle principal — l'y mettre ne coûte rien et ne touche pas au
// code-splitting du module lui-même.
export const ARGOS_VIEWS = [
  "argos", "argos-entreprise", "argos-personne", "argos-graphe",
  "argos-surveillance", "argos-dossiers", "argos-sources", "argos-journal",
];

export const NAV_ADMIN_ITEMS = [
  {route:"admin", label:"Gestion des membres", icon:"users"},
  {route:"admin-acces", label:"Gouvernance des accès", icon:"key"},
  {route:"admin-super-lists", label:"Listes membres", icon:"list-details"},
  // « Demandes de compte » : les demandes d'engagement sont parties dans
  // Activité (athos-demandes, lot L11d-bis, 30/09/2026) ; la page garde les
  // demandes de création de compte et renvoie vers Activité.
  {route:"admin-demandes", label:"Demandes de compte", icon:"user"},
  {route:"admin-campagnes", label:"Campagnes de relance", icon:"speakerphone"},
  // « Budget & cotisations » et « Dépenses & paiements » : retirés d'ici le
  // 29/09/2026. Ils appartiennent à Pilotage (MODULE_VIEWS.pilotage) et une vue
  // n'appartient qu'à un module ; dans le rail, Pilotage précède Administration.
  {route:"admin-emails-auto", label:"Emails hebdomadaires", icon:"mail"},
  {route:"admin-envois", label:"Suivi des envois", icon:"mail-check"},
  {route:"admin-erreurs", label:"Erreurs front", icon:"bug"},
  {route:"admin-auth-mesure", label:"Mesure d'authentification", icon:"fingerprint"},
  // Lot 0 (11/10/2026) : l'état de toutes les collectes en une page.
  {route:"admin-sante-collectes", label:"Santé des collectes", icon:"activity"},
  {route:"admin-adoption", label:"Adoption & usage", icon:"chart-bar"},
  // F1 (11/10/2026) : ouvertures des modules, agrégées par jour, module et
  // mutuelle ; la vue « Par personne » n'y paraît que pour le
  // super-administrateur et les lecteurs qu'il désigne (src/AdminUsagesPanel.js).
  {route:"admin-usages", label:"Usages d'Aramis", icon:"chart-line"},
  // Les nouveautés d'Aramis (demande de Pierre du 18/10/2026) : un numéro, deux
  // éditions (utilisateurs ; administrateurs et équipe d'Enio), envoyées après
  // validation ; brouillon du vendredi. src/nouveautes/NouveautesPage.js.
  {route:"admin-nouveautes", label:"Les nouveautés d'Aramis", icon:"news"},
  {route:"emailing", label:"Emailing direct", icon:"send"},
  // Tous les échanges avec Cowork sur une page (demande de Pierre du
  // 13/10/2026) : état d'ensemble, « Tout synchroniser », une carte et les
  // réglages de chaque échange, journal. src/synchronisation/SynchronisationPage.js
  // (sa propre racine). Venue de la Super administration le 19/10/2026
  // (décision de Pierre) : les administrateurs la lisent et synchronisent ;
  // les réglages sensibles restent au super-administrateur (chaque fonction
  // le garde, chaque carte le dit). L'adresse « super-synchronisation » reste
  // la même (favoris, liens des pages métier).
  {route:"super-synchronisation", label:"Synchronisation", icon:"refresh", reserveAdmin:true},
];
export const NAV_SUPERADMIN_ITEMS = [
  {route:"admin-super-admins", label:"Comptes admin", icon:"lock"},
  // Demandes d'engagement et réinitialisations de compte à valider (18/10/2026 :
  // la rangée de la page, qui seule y menait, est masquée quand le rail porte
  // le sommaire).
  {route:"admin-super-demandes", label:"Demandes à valider", icon:"inbox"},
  {route:"admin-super-mutuelles", label:"Mutuelles", icon:"building-community"},
  {route:"admin-super-sharepoint", label:"SharePoint", icon:"folder"},
  {route:"admin-super-security", label:"Sécurité", icon:"shield"},
  // Qui reçoit les alertes (décision de Pierre du 11/10/2026 : plus aucune
  // adresse écrite dans le code). DestinatairesAlertesPanel.js.
  {route:"admin-super-alertes", label:"Destinataires des alertes", icon:"bell"},
  // Modèle par usage de l'IA et plafonds mensuels de dépense (lot 0, P0-3,
  // 11/10/2026). ModelesPlafondsIAPanel.js.
  {route:"admin-super-ia", label:"Modèles et plafonds de l'IA", icon:"sparkles"},
  {route:"admin-super-logs", label:"Logs d'activité", icon:"history"},
  {route:"admin-super-backup", label:"Sauvegarde & continuité", icon:"database"},
  {route:"admin-super-diagnostic", label:"Diagnostic & dépannage", icon:"settings"},
];
// Onglets du super-admin RETIRÉS : leur ancienne adresse (admin-super-<onglet> :
// favoris, vue mémorisée) ouvre le panneau sur un onglet VIVANT, jamais une page
// vide (SuperAdminPanel.js). Ce n'est pas un LEGACY_REDIRECTS : la famille
// admin-super-* reste servie par le panneau ; l'adresse retirée est rattachée
// (MODULE_VIEWS) au module de l'onglet qui la sert.
// « gamification » : barème de points retiré au lot L1a (27/09/2026).
export const ONGLETS_SUPERADMIN_RETIRES = { gamification: "lists" };
/** Page du menu qui SERT une vue : l'adresse d'un onglet retiré allume l'onglet
 *  vivant (fil, filet du rail et du tiroir) ; toute autre vue est rendue telle quelle. */
// Pages sans entrée propre servies par une entrée de menu : la page d'un
// objectif d'Hestia (lot H0) et le Pilotage d'Hestia (sans entrée dans
// Pilotage depuis le 04/10/2026 : on y entre par le bouton « Pilotage
// d'Hestia » de la page Hestia, demande de Pierre) allument « Hestia » dans le
// rail et le fil. Un album de la Médiathèque (lot L17.2a) allume « Médiathèque ».
// La fiche d'une note institutionnelle et les modèles de note (B6) allument
// « Notes institutionnelles ». La fiche d'un risque allume « Tableau des
// risques », celle d'une procédure « Procédures » (contrôle interne, D2), celle
// d'une mission d'audit « Audit interne » (12/10/2026 : leurs rangées de retour
// se masquent sous le rail, il doit montrer où l'on est). La
// fiche d'un thème de la doctrine, l'import d'un premier jet et la file des
// propositions des membres allument « Doctrine d'Enio », comme les débats et la
// fiche d'un débat (lot L4) ; « Mes propositions » allume « Positions d'Enio ».
// La fiche d'un parlementaire allume « Parlementaires »,
// celle d'un scrutin « Votes et scrutins », celle d'un amendement « Textes de
// loi » (campagne PLF et PLFSS comme dossiers suivis), celle d'un article de
// loi « Le texte en clair » (lot E). La reprise des registres de Calypso
// (21/10/2026) allume « RGPD (Calypso) ».
const PAGES_SERVIES = { "hestia-objectif": "hestia", "pilotage-hestia": "hestia", "mediatheque-album": "mediatheque",
  "note-institutionnelle": "notes-institutionnelles", "notes-institutionnelles-modeles": "notes-institutionnelles",
  risque: "risques-tableau", procedure: "procedures", "audit-mission": "audit", "audit-plan": "audit-plans", "audit-constat": "audit-registre",
  "conformite-propositions": "controle-interne",
  "doctrine-theme": "doctrine", "doctrine-import": "doctrine", "doctrine-propositions": "doctrine",
  "doctrine-debats": "doctrine", "doctrine-debat": "doctrine",
  "doctrine-mes-propositions": "doctrine-positions",
  parlementaire: "parlementaires", scrutin: "votes-et-scrutins", amendement: "textes-de-loi", "article-de-loi": "texte-en-clair",
  "hygie-reprise": "hygie" };
export const pageServie = (view) => {
  if (Object.prototype.hasOwnProperty.call(PAGES_SERVIES, view)) return PAGES_SERVIES[view];
  const o = /^admin-super-(\w+)$/.exec(view || "");
  return o && Object.prototype.hasOwnProperty.call(ONGLETS_SUPERADMIN_RETIRES, o[1]) ? "admin-super-" + ONGLETS_SUPERADMIN_RETIRES[o[1]] : view;
};

// ── ADMINISTRATION DANS LE RAIL (demande de Pierre du 29/09/2026) ────────────
// « Administration » et « Super administration » quittent « Moi » pour le rail,
// APRÈS Pilotage, pour qui y a accès : « tout au même endroit ». Ce ne sont PAS
// des modules métier : ni teinte de NAV_COLORS (TEINTE_ADMIN, neutre), ni tuile
// de la grille de la feuille « Modules » (section à part), ni porte de
// PORTAILS_MODULES (la porte est la première page), ni raccourci ⌥1 à ⌥0. Même
// forme qu'un module de buildNavModules() : le rail (sommaire en accordéon),
// le sommaire en tiroir et moduleForView les traitent comme tels. Les pages
// viennent de NAV_ADMIN_ITEMS et NAV_SUPERADMIN_ITEMS, jamais recopiées.
// Verrou : src/navAdmin.test.js.
export const LIBELLES_ADMIN = { admin: "Administration", superadmin: "Super administration" };
const ITEMS_ADMIN = { admin: NAV_ADMIN_ITEMS, superadmin: NAV_SUPERADMIN_ITEMS };
const moduleAdmin = (key, subtitle, icon, show) => ({ key, label: LIBELLES_ADMIN[key], subtitle, color: TEINTE_ADMIN, icon,
  show: !!show, accueil: null, items: ITEMS_ADMIN[key].map((it) => ({ ...it, color: TEINTE_ADMIN })) });
/** Droits de la session RÉELLE, jamais ceux de la personne affichée en « Voir
 *  comme » : ce sont eux que lisent les gardes des pages (App.jsx).
 *  @param isAdmin      rôle admin ou superadmin
 *  @param isSuperAdmin rôle superadmin, strictement */
export const buildNavAdmin = ({ isAdmin, isSuperAdmin }) => [
  moduleAdmin("admin", "Équipe Enio", "settings", isAdmin),
  moduleAdmin("superadmin", "Comptes, sécurité, continuité", "shield-lock", isSuperAdmin),
];

// ── MON ESPACE SALARIÉ DANS LE RAIL (demande de Pierre du 26/10/2026) ────────
// « Un vrai cockpit, avec ses menus » : l'espace salarié a son sommaire, comme
// un module, mais ce n'est PAS un onzième module métier. C'est l'espace
// PERSONNEL de qui a un compte salarié (rhComptes, porte Aramis) : il vit dans
// le rail juste sous « Ma semaine », avant les modules, à la teinte navy des
// écrans personnels (EnTetePage module="moi"). Ni teinte de NAV_COLORS, ni
// tuile dans la grille de la feuille « Modules », ni porte de
// PORTAILS_MODULES, ni raccourci ⌥ : au doigt, on y entre par « Moi ». Même
// forme qu'un module de buildNavModules() : le rail (sommaire en accordéon),
// le tiroir du sommaire, l'onglet contextuel de la barre basse, ⌘K et
// moduleForView le lisent tel quel (buildNavEspaceSalarie, plus bas).
// Chaque espace a SA teinte de charte, dérivée de NAV_COLORS : sa carte
// colorée au cockpit (EspaceSalarieCockpit.js), sa tuile au tiroir et dans
// l'en-tête de sa page. « Mes notes de frais » est la page « mes-notes-frais »
// elle-même (rattachée ici : elle n'appartenait à aucun module) ; « Mon
// équipe », l'espace des managers seuls. `rubrique` : le nom de l'espace au
// portail autonome /espace-salarie, qui n'a pas de vues. Verrou :
// src/espaceSalarieNav.test.js.
export const LIBELLE_ESPACE_SALARIE = "Mon espace salarié";
export const TEINTE_ESPACE_SALARIE = NAV_COLORS.pilotage;
export const ACCUEIL_ESPACE_SALARIE = Object.freeze({ rubrique: "accueil", route: "mon-espace-salarie", label: "Accueil", icon: "home",
  description: "Ce qui vous attend, et vos espaces en un coup d'œil." });
export const NAV_ESPACE_SALARIE_ITEMS = Object.freeze([
  { rubrique: "dossier", route: "mon-espace-salarie-dossier", label: "Mon dossier", icon: "address-book", color: NAV_COLORS.pilotage,
    description: "Votre poste, votre contrat et votre classification." },
  { rubrique: "entretiens", route: "mon-espace-salarie-entretiens", label: "Mes entretiens", icon: "calendar-time", color: NAV_COLORS.gouv,
    description: "Entretiens annuel et professionnel : préparation, comptes-rendus et visa." },
  { rubrique: "formations", route: "mon-espace-salarie-formations", label: "Mes formations", icon: "school", color: NAV_COLORS.athos,
    description: "Vos avis à chaud et à froid, vos formations suivies et leurs attestations." },
  { rubrique: "notes-frais", route: "mes-notes-frais", label: "Mes notes de frais", icon: "luggage", color: NAV_COLORS.mutuelles, reserve: "ndf",
    description: "Photographier un justificatif, suivre un remboursement, valider une note." },
  { rubrique: "competences", route: "mon-espace-salarie-competences", label: "Mes compétences", icon: "puzzle", color: NAV_COLORS.exploration,
    description: "Vos compétences reconnues, celles à confirmer en entretien, vos diplômes et certificats." },
  { rubrique: "demandes", route: "mon-espace-salarie-demandes", label: "Mes demandes", icon: "send", color: NAV_COLORS.events,
    description: "Une formation, une évolution, une autre demande à votre service RH." },
  { rubrique: "equipe", route: "mon-espace-salarie-equipe", label: "Mon équipe", icon: "users", color: NAV_COLORS.rh, reserve: "manager",
    description: "Votre espace de manager : avis à rendre, entretiens et compétences de votre équipe." },
].map((it) => Object.freeze(it)));
/** L'espace d'une vue (« entretiens »), ou null hors de l'espace salarié. */
export const rubriqueEspaceSalarie = (view) => (view === ACCUEIL_ESPACE_SALARIE.route ? "accueil"
  : (NAV_ESPACE_SALARIE_ITEMS.find((it) => it.route === view) || {}).rubrique || null);

// Boîte à outils budgétaire : réservée à son propriétaire (chantier A). Verrou
// UX front sur l'e-mail de session ; la vraie barrière est côté serveur
// (exigerProprietaireBudget sur les 7 CFs budgetPrep). Même e-mail que le back.
export const PROPRIETAIRE_BUDGET_EMAIL = "pierre.marini@enio.fr";
export const estProprietaireBudget = (session) =>
  (session?.email || "").toLowerCase().trim() === PROPRIETAIRE_BUDGET_EMAIL;

// Pages du module RH — source UNIQUE : menu principal (desktop + mobile) ET
// barre de navigation interne du module (RHNavBar, lot C 21/08/2026).
export const RH_NAV_ITEMS = [
  // Recrutement
  {route:"rh-banque-cv", color:"#94C11F", label:"CV reçus", icon:"inbox", group:"Recrutement", groupColor:"#94C11F", description:"Les CV reçus, partagés entre mutuelles.",
    children:[
      {route:"rh-import-email", label:"Réception des CV par e-mail", icon:"mail-down", color:"#1D948A"},
    ]},
  {route:"rh-offres-emploi", color:"#1D948A", label:"Offres d'emploi", icon:"briefcase", group:"Recrutement", description:"Les offres d'emploi publiées.",
    children:[
      // Modèles : RH Maître ou administrateur (`rhMaitre`), comme RHModule.
      {route:"rh-pipeline-templates", label:"Modèles de processus", icon:"template", color:"#F49800", rhMaitre:true},
      {route:"rh-scorecard-templates", label:"Grilles d'évaluation", icon:"clipboard-check", color:"#951B81", rhMaitre:true},
      {route:"rh-email-templates", label:"Modèles d'e-mails candidats", icon:"mail", color:"#F49800", rhMaitre:true},
    ]},
  {route:"rh-candidatures", color:"#F49800", label:"Candidatures", icon:"clipboard-list", group:"Recrutement", description:"Les candidatures reçues (spontanées, e-mail, page Carrières).",
    children:[
      {route:"rh-pipeline", label:"Tableau de suivi (kanban)", icon:"layout-kanban", color:"#951B81"},
    ]},
  {route:"rh-matching", color:"#951B81", label:"Suggestions CV et offres", icon:"target", group:"Recrutement", description:"Rapprochements automatiques entre CV et offres."},
  // « Vivier partagé » et « Configuration » : administrateurs d'Enio seuls,
  // comme RHModule (AccessDenied sinon) et l'accueil RH (11/10/2026, la page
  // et le rail montrent les mêmes entrées : src/accueilsAlignes.test.js).
  {route:"rh-talent-pool", color:"#D60B52", label:"Vivier partagé", icon:"sparkles", group:"Recrutement", description:"Le vivier de talents partagé du réseau.", adminOnly:true},
  // Personnel & carrières (SIRH)
  {route:"rh-effectifs", color:"#2AA9E1", label:"Registre du personnel", icon:"users", group:"Personnel & carrières", groupColor:"#1D948A", description:"Effectifs : contrats, classification CCN, rémunération, compétences."},
  {route:"rh-organigramme", color:"#2AA9E1", label:"Organigramme", icon:"sitemap", group:"Personnel & carrières", description:"Rattachements hiérarchiques et fonctionnels de la structure."},
  // Demande de Pierre du 15/10/2026 : « Remonte Fiches de poste dans le Menu
  // (pas uniquement au fond de GPEC) ». Rangée avec le personnel, dont elle
  // décrit les postes, et non dans « Formation & GPEC », qui la CONSOMME.
  {route:"rh-fiches-poste", color:C.orange, label:"Fiches de poste", icon:"file-text", group:"Personnel & carrières", description:"Intitulés, classification CCN, missions et compétences requises ; import Word ou PDF."},
  {route:"rh-entretiens", color:"#951B81", label:"Entretiens (EAE / EPP)", icon:"messages", group:"Personnel & carrières", description:"Entretiens annuels d'évaluation et de parcours professionnel."},
  {route:"rh-comptes", color:"#1D948A", label:"Accès & demandes salariés", icon:"key", group:"Personnel & carrières", description:"Espaces personnels des salariés et traitement de leurs demandes."},
  {route:"rh-taches", color:"#94C11F", label:"Tâches & processus RH", icon:"checklist", group:"Personnel & carrières", description:"Checklists (onboarding…), actions assignées et relances automatiques."},
  {route:"rh-simulateur-paie", color:"#F49800", label:"Simulateur de paie", icon:"calculator", group:"Personnel & carrières", description:"Coût employeur, primes et avantages, calibrable avec vos bulletins."},
  // Formation & GPEC
  {route:"rh-formations-catalogue", color:"#F49800", label:"Formations référencées", icon:"books", group:"Formation & GPEC", groupColor:"#F49800", description:"Catalogue, organismes, formateurs et avis (notes ★)."},
  {route:"rh-plan-formation", color:"#94C11F", label:"Plan de formation", icon:"list-details", group:"Formation & GPEC", description:"Besoins, arbitrage et budget, avec suggestions du catalogue."},
  {route:"rh-formations-sessions", color:"#2AA9E1", label:"Sessions & inscriptions", icon:"calendar", group:"Formation & GPEC", description:"Sessions, convocations et enquêtes à chaud."},
  {route:"rh-formations-suivi", color:"#1D948A", label:"Suivi & habilitations", icon:"school", group:"Formation & GPEC", description:"Heures réalisées et habilitations à renouveler."},
  // Mes formations (lots 1 et 2, 16/10/2026) : organiser le suivi d'une session
  // (émargement à jeton, reprise d'après la feuille de l'organisme, envois) et
  // lire les fiches « formation suivie » des salariés de l'espace.
  {route:"rh-formations-suivies", color:C.teal, label:"Formations suivies & émargement", icon:"signature", group:"Formation & GPEC", description:"Émargement en ligne, reprise d'après la feuille de l'organisme, attestations, fiches de vos salariés formés."},
  {route:"rh-registre-formation", color:"#D60B52", label:"Registre mutualisé", icon:"database", group:"Formation & GPEC", description:"Plans de formation consolidés de toutes les mutuelles : benchmark et mutualisation."},
  {route:"rh-competences", color:"#951B81", label:"Compétences & GPEC", icon:"puzzle", group:"Formation & GPEC", description:"Cartographie des compétences : visées, requises, détenues, et écarts."},
  // Pilotage & conformité
  {route:"rh-statistiques", color:"#003F59", label:"Tableaux de bord", icon:"chart-bar", group:"Pilotage & conformité", groupColor:"#951B81", description:"Les tableaux de bord RH (recrutement, effectifs, formation)."},
  {route:"rh-bilan-social", color:"#2AA9E1", label:"Bilan social & Index égalité", icon:"chart-line", group:"Pilotage & conformité", description:"Bilan social et index d'égalité."},
  {route:"rh-conservation", color:"#D60B52", label:"RGPD & conservation", icon:"archive", group:"Pilotage & conformité", description:"Durées de conservation (référentiel CNIL) et purge du vivier."},
  // « Personnalisation » : RH Maître ou administrateur (`rhMaitre`), comme
  // RHModule ; hors de « Configuration » (administrateurs seuls), où le RH
  // Maître ne la voyait pas (11/10/2026).
  {route:"rh-personnalisation", color:"#94C11F", label:"Personnalisation", icon:"palette", group:"Pilotage & conformité", description:"Couleurs, logo et taille du logo de l'espace RH de la mutuelle.", rhMaitre:true},
  {route:"rh-admin-config", color:"#003F59", label:"Configuration", icon:"settings", group:"Pilotage & conformité", description:"Configuration du module RH.", adminOnly:true,
    children:[
      {route:"rh-recruteurs", label:"Recruteurs & rôles RH", icon:"user-cog", color:"#1D948A", adminOnly:true},
      {route:"rh-co-recrutement", label:"Co-recrutement inter-mutuelles", icon:"heart-handshake", color:"#951B81", adminOnly:true},
    ]},
  // Guide
  {route:"rh-lisez-moi", color:"#003F59", label:"Lisez-moi (guide)", icon:"book", group:"Guide", groupColor:"#003F59", description:"Le guide de l'espace RH : recrutement, e-mails candidats, SIRH, formation, RGPD."},
];

// Vues appartenant à chaque module — source UNIQUE (ex-constantes locales de
// Header.js) : sert au surlignage du menu principal ET à la barre latérale
// générique (ModuleSidebar) pour savoir dans quel module on se trouve, y
// compris sur les sous-pages hors menu (détails, drawers, séances…).
export const MODULE_VIEWS = {
  // « accueil » : ancienne page d'accueil, redirigée vers Ma semaine (lot L11p).
  maSemaine: ["ma-semaine", "accueil"],
  // Espace personnel (« Moi ») : n'est PAS un module de buildNavModules (pas de
  // barre latérale, pas de surlignage d'en-tête). Sert à rattacher les vues
  // personnelles et la vue historique « classement » (LEGACY_REDIRECTS).
  // « Mes formations » (16/10/2026) : ouverte depuis « Moi », visible par la
  // personne seule (ses fiches « formation suivie »).
  moi: ["mon-profil", "classement", "mes-formations"],
  // « athos-dashboard » (lot L11d), « athos-home-classique »,
  // « athos-migration-review » et « athos-missions-statutaires » (lot L11p) :
  // vues retirées du menu, gardées ici pour le surlignage pendant leur
  // redirection (LEGACY_REDIRECTS).
  athos: ["accueil-athos","athos","athos-demandes","athos-home-classique","athos-engagement-mutuelle","athos-dashboard","ressources","athos-projets","athos-solutions","athos-missions","athos-projets-classique","athos-reunions","athos-ma-mutuelle","athos-domaines-poles","athos-noota","athos-migration-review","athos-missions-statutaires"],
  // Affaires publiques : module à part depuis le 12/10/2026 (décision de
  // Pierre : « un module assez solide pour être identifié à part »), sorti
  // d'Activité, où ces vues vivaient. Aucune vue n'a changé de nom : favoris,
  // liens des courriels et vues mémorisées restent valides tels quels.
  ap: [
    // La page d'accueil du module (02/10/2026, l'ancienne entrée d'Activité) et le Radar Présidentielle 2027.
    "affaires-publiques","radar-presidentielle-2027",
    // Module PLF & PLFSS (04/10/2026) : visible des administrateurs jusqu'à la phase C.
    "plf-plfss-2027",
    // Rapports AMO-AMC (05/10/2026) : au menu des administrateurs le temps de la
    // relecture du contenu par Pierre.
    "rapports-amo-amc",
    // Textes de loi (lot B1.2 de « Tous les textes de loi », 11/10/2026).
    "textes-de-loi",
    // Refonte des Affaires publiques (12/10/2026, maquettes v1 et v2) : le
    // texte en clair, Parlementaires, Votes et scrutins.
    "texte-en-clair","parlementaires","votes-et-scrutins",
    // La veille juridique et réglementaire (décision de Pierre du 18/10/2026) :
    // UNE veille, dans l'écosystème des Affaires publiques (elle vivait dans
    // Conformité, dont la page d'accueil garde une porte vers elle) ; le nom
    // de la vue ne change pas (liens des courriels, favoris).
    "veille-juridique",
    // La fiche d'un parlementaire en pleine page (lot B, ?elu=), sous-page de
    // Parlementaires, celle d'un scrutin (lot C, ?uid=), sous-page de Votes
    // et scrutins, celle d'un amendement (lot D, ?uid=AMANR5L…), sous-page
    // de Textes de loi, et celle d'un article de loi (lot E, ?texte=…&article=…
    // ou ?dossier=DLR…&article=…), sous-page du texte en clair (PAGES_SERVIES).
    "parlementaire","scrutin","amendement","article-de-loi",
    // Notes institutionnelles (B6, 11/10/2026), administrateurs seuls : la
    // liste (entrée du sommaire), la fiche d'une note (?note=<id>) et les
    // modèles de note, sous-pages ouvertes depuis la liste (PAGES_SERVIES).
    "notes-institutionnelles","note-institutionnelle","notes-institutionnelles-modeles",
    // Doctrine d'Enio (socle, 11/10/2026) : l'atelier, équipe d'Enio seule :
    // l'index des thèmes (entrée du sommaire), la fiche d'un thème
    // (?theme=<id>) et l'import d'un premier jet, sous-pages ouvertes depuis
    // l'index (PAGES_SERVIES).
    "doctrine","doctrine-theme","doctrine-import",
    // La file des propositions des membres (doctrine ouverte, lot L3), sous-page
    // de l'atelier ouverte depuis l'index (PAGES_SERVIES).
    "doctrine-propositions",
    // Les débats (doctrine ouverte, lot L4) : la liste et la fiche d'un débat
    // (?debat=<id>), sous-pages de l'atelier ouvertes depuis l'index (PAGES_SERVIES).
    "doctrine-debats","doctrine-debat",
    // Positions d'Enio (doctrine ouverte, lot L2, 12/10/2026) : la doctrine
    // ouverte à tous les membres, les synthèses publiées par thème ; « Mes
    // propositions » (lot L3), sous-page ouverte depuis elle et depuis la
    // cloche de la réponse (PAGES_SERVIES).
    "doctrine-positions","doctrine-mes-propositions",
    // Notes d'Enio reçues (lot L4, 12/10/2026) : les notes validées diffusées
    // à la personne connectée (?diffusion=<id> pour en lire une).
    "notes-recues"],
  exploration: ["accueil-marches","exploration","veille"],
  mutuelles: ["accueil-mutuelles","athos-annuaire","athos-implantations","dataviz","nouvelles-mutuelles","referentiel-organismes",
    // Les mutuelles en clair (11/10/2026) : SFCR publics lus et vérifiés.
    "mutuelles-en-clair",
    // Fondations et fonds de dotation des mutuelles (18/10/2026).
    "fondations-mutuelles"],
  // « strategie-2030 » (gouv) et « gel-avoirs-ppe » (hygie) : hors menu depuis
  // le 30/09/2026, gardées ici pour que leurs liens entrants (courriels,
  // favoris) ouvrent la page dans son module, surlignage compris.
  // « strategie-2030 » ne s'ouvre plus : consultation retirée le 01/10/2026,
  // la vue redirige à jamais vers l'accueil Gouvernance (LEGACY_REDIRECTS).
  // « themis-documents-seance » (guichet des pièces d'une séance, lien de la
  // convocation) : hors menu, dans le module depuis le lot L11s (rail, fil
  // « Enio / Gouvernance »), comme les pages d'instance où il est intégré.
  gouv: ["accueil-gouv","agora","participant","results","screen","ideas",...GOUVERNANCE_VIEWS,"agora-admin","questions","questions-historique","consultations","strategie-2030","travail-groupes","themis-documents-seance",
    // Hestia (lot H0, 01/10/2026) : accueil, page d'un objectif (?o=1 à 7), « À traiter ».
    // Pilotage d'Hestia (administrateurs) : rattaché à Hestia depuis le 04/10/2026,
    // sans entrée de menu (bouton « Pilotage d'Hestia » de la page Hestia).
    "hestia","hestia-objectif","hestia-a-traiter","pilotage-hestia"],
  events: ["themis-evenements","themis-events-list","event-fiche","event-suivi","themis-evenements-validation","themis-contacts","themis-pending-updates","event-day","themis-handwritten","event-table-plan","event-guest-list","event-rsvp-inbox","event-invitation-requests",
    // Médiathèque (lot L17.2a, 04/10/2026) : accueil et album (?album=<id>),
    // ouverts aux membres (le serveur décide de chaque album, règle M1).
    "mediatheque","mediatheque-album"],
  rh: ["accueil-rh","rh","rh-banque-cv","rh-cv-detail","rh-offres-emploi","rh-offre-detail","rh-candidatures","rh-matching","rh-statistiques","rh-admin-config","rh-conservation","rh-personnalisation","rh-effectifs","rh-entretiens","rh-comptes","rh-import-email","rh-pipeline-templates","rh-scorecard-templates","rh-email-templates","rh-formations-catalogue","rh-formations-sessions","rh-plan-formation","rh-formations-suivi","rh-formations-suivies","rh-registre-formation","rh-organigramme","rh-fiches-poste","rh-competences","rh-taches","rh-simulateur-paie","rh-bilan-social","rh-lisez-moi","rh-recruteurs","rh-pipeline","rh-talent-pool","rh-co-recrutement"],
  hygie: ["hygie","hygie-registre","hygie-aipd","hygie-dsar","hygie-violations","hygie-subprocessors","hygie-formation","hygie-dashboard","hygie-library","hygie-library-admin","hygie-forum","hygie-annuaire","gel-avoirs-ppe","accueil-conformite",
    // Veille RGPD de Calypso : retirée le 21/10/2026, valide à jamais
    // (LEGACY_REDIRECTS : Affaires publiques › Veille juridique et réglementaire).
    "hygie-veille",
    // Reprise des registres de Calypso en clair (21/10/2026) : sous-page
    // ouverte depuis l'accueil de Calypso (note « Reprise des registres »).
    "hygie-reprise",
    // Audits (D1, 11/10/2026) : missions de la mutuelle, fiche d'une mission
    // (?mission=<id>, sous-page ouverte depuis la liste), actions en cours,
    // liste d'accès (DG) et modèles de grilles (super-administrateur).
    "audit","audit-mission","audit-actions","audit-acces","audit-modeles","audit-plans","audit-plan","audit-registre","audit-constat",
    // Contrôle interne (D2, 11/10/2026) : index, cartographie et tableau des
    // risques, fiche d'un risque (?risque=<id>), procédures, fiche d'une
    // procédure (?procedure=<id>), référentiel commun (super-administrateur).
    "controle-interne","risques","risques-tableau","risque","procedures","procedure","controle-interne-referentiel","controle-interne-acces","parametrage-mutuelle",
    // Échange de la Conformité avec Cowork (12/10/2026) : bibliothèque des
    // risques types ; propositions communes de Cowork (super-administrateur,
    // sous-page ouverte depuis le contrôle interne, 13/10/2026).
    "risques-bibliotheque","conformite-propositions","conformite-demonstration",
    // Retirées du menu le 13/10/2026, valides à jamais (LEGACY_REDIRECTS) ;
    // « conformite-cowork » mène à Super administration › Synchronisation.
    "risques-propositions","audit-propositions","conformite-cowork"],
  // Pilotage porte aussi la PRÉPARATION des séances (décision Pierre 04/09/2026 :
  // « déplacer la page de préparation des instances dans Pilotage ») et ses
  // anciennes strings Thémis, gardées valides à jamais (redirections dans App.jsx).
  pilotage: ["accueil-pilotage","pilotage-seances","seance-emargement","seance-scrutins","admin-vote-ag","admin-budget","admin-budget-outils","admin-depenses","admin-ndf","compta-ec",
    // Boîte à outils budgétaire éclatée en 5 écrans (09/09/2026). L'ancienne
    // vue « admin-budget-outils » reste VALIDE À JAMAIS (favoris, liens
    // e-mail, vue mémorisée) : elle redirige via LEGACY_REDIRECTS.
    "budget-accueil","budget-simulation","budget-exercice","budget-situation","budget-depenses","budget-compte","budget-paie",
    "preparer-presentation","historiques",
    "themis","themis-instances","themis-preparer","themis-seance","themis-live","themis-historique"],
  argos: ARGOS_VIEWS,
  // Administration et Super administration (rail, 29/09/2026), plus deux
  // adresses du panneau hors menu : l'onglet retiré (sert « Listes membres »)
  // et « Demandes » du super-admin.
  admin: [...NAV_ADMIN_ITEMS.map((it) => it.route), "admin-super-gamification"],
  superadmin: NAV_SUPERADMIN_ITEMS.map((it) => it.route),
  // Mon espace salarié (26/10/2026) : l'accueil (le cockpit) et ses espaces,
  // « mes-notes-frais » compris (buildNavEspaceSalarie). Pour qui n'a pas
  // d'espace salarié, ces vues restent hors module (show:false).
  salarie: [ACCUEIL_ESPACE_SALARIE.route, ...NAV_ESPACE_SALARIE_ITEMS.map((it) => it.route)],
};

// ── COMPTEURS DES ENTRÉES DE MENU (lot L11d-bis, 30/09/2026) ─────────────────
// buildNavModules reçoit `compteurs` ({ route: n }) et pose `compteur` sur
// l'entrée VISIBLE qui porte cette route : c'est la source unique du rail, du
// tiroir, de la feuille « Modules » et de la vitrine. Chiffre EXACT, jamais
// plafonné ; 0, null ou une valeur non entière : pas de pastille.
// Libellé lu par les lecteurs d'écran, au singulier comme au pluriel.
export const LIBELLES_COMPTEURS = {
  "athos-demandes": ["demande d'engagement à valider", "demandes d'engagement à valider"],
  // Mon espace salarié (26/10/2026) : chiffres du cockpit (espaceSalarieSynthese.js).
  "mon-espace-salarie": ["action à traiter", "actions à traiter"],
  "mon-espace-salarie-entretiens": ["entretien à préparer ou à viser", "entretiens à préparer ou à viser"],
  "mon-espace-salarie-formations": ["avis de formation à donner", "avis de formation à donner"],
  "mes-notes-frais": ["note de frais à traiter", "notes de frais à traiter"],
  "mon-espace-salarie-equipe": ["action pour votre équipe", "actions pour votre équipe"],
};
const nombreCompteur = (v) => (Number.isInteger(v) && v > 0 ? v : 0);
/** « 3 demandes d'engagement à valider » (n > 0). */
export const libelleCompteur = (route, n) => {
  const l = LIBELLES_COMPTEURS[route] || ["élément à traiter", "éléments à traiter"];
  return n + " " + (n > 1 ? l[1] : l[0]);
};
/** Pastille d'une entrée : { n, libelle } ou null. */
export const compteEntree = (e) => {
  const n = nombreCompteur(e && e.compteur);
  return n ? { n, libelle: libelleCompteur(e.route, n) } : null;
};
/** Pastille d'un module (ligne du rail, tuile de la feuille « Modules ») : la
 *  SOMME des compteurs de ses entrées visibles ; null sans compteur. */
export const compteModule = (m) => {
  const comptes = entreesModule(m).map(compteEntree).filter(Boolean);
  if (!comptes.length) return null;
  return { n: comptes.reduce((t, c) => t + c.n, 0), libelle: comptes.map((c) => c.libelle).join(", ") };
};
function poserCompteurs(modules, compteurs) {
  if (!compteurs) return modules;
  const poser = (it) => {
    if (!it || it.show === false) return it;
    const n = nombreCompteur(compteurs[it.route]);
    const enfants = Array.isArray(it.children) ? it.children.map(poser) : null;
    if (!n && !enfants) return it;
    return { ...it, ...(n ? { compteur: n } : {}), ...(enfants ? { children: enfants } : {}) };
  };
  return modules.map((m) => (m.show === false ? m : { ...m, items: (m.items || []).map(poser) }));
}

/** « Mon espace salarié » (26/10/2026), de la forme d'un module (voir
 *  NAV_ESPACE_SALARIE_ITEMS). Droits de la personne CONNECTÉE, lus par le
 *  serveur (rhSelfEspaceDisponible, apNdfMonAcces) : jamais une entrée morte.
 *  @param disponible  un espace salarié s'ouvre pour elle
 *  @param estManager  elle encadre une équipe (« Mon équipe »)
 *  @param ndf         les notes de frais lui sont ouvertes
 *  @param compteurs   { route: n } : ce qui attend un acte (cockpit) */
export function buildNavEspaceSalarie({ disponible = false, estManager = false, ndf = false, compteurs = null } = {}) {
  const ouvert = (it) => (it.reserve === "manager" ? !!estManager : it.reserve === "ndf" ? !!ndf : true);
  const poser = (it) => {
    const n = nombreCompteur(compteurs && compteurs[it.route]);
    return n ? { ...it, compteur: n } : { ...it };
  };
  return [{
    key: "salarie", label: LIBELLE_ESPACE_SALARIE, court: "Mon espace", subtitle: "Entretiens, formations, notes de frais",
    color: TEINTE_ESPACE_SALARIE, icon: "briefcase", show: !!disponible, personnel: true,
    accueil: poser(ACCUEIL_ESPACE_SALARIE),
    items: NAV_ESPACE_SALARIE_ITEMS.map((it) => (ouvert(it) ? poser(it) : { ...it, show: false })),
  }];
}

/** Les pages du Budget, dans l'ordre du sommaire (07/10/2026) : la MÊME liste
 *  sert le rail, la feuille « Modules », la palette et l'accueil du Budget
 *  (BudgetToolboxPanel, écran « budget-accueil »). */
export const BUDGET_PAGES = Object.freeze([
  {route:"budget-exercice", label:"Consommation", icon:"chart-bar", description:"Où l'on en est de l'exercice, poste par poste : consommé, enveloppe et écart."},
  {route:"budget-simulation", label:"Simuler & planifier", icon:"chart-line", color:"#1D948A", description:"Les exercices suivants : hypothèses, scénarios et cotisations."},
  {route:"budget-situation", label:"Trésorerie", icon:"cash-banknote", color:"#003F59", description:"Du solde du 1er janvier au solde attendu au 31 décembre, mois par mois."},
  {route:"budget-depenses", label:"Dépenses & arbitrages", icon:"report-money", color:"#F49800", description:"Où va l'argent, fournisseur par fournisseur, et les lignes à classer."},
  {route:"budget-compte", label:"Vision par compte", icon:"notebook", color:"#951B81", description:"Le budget lu compte par compte, du plan comptable."},
  {route:"budget-paie", label:"Paie : bulletins & fiches", icon:"file-text", color:"#94C11F", description:"Import des bulletins, fiches des salariés et masse salariale."},
]);

export function buildNavModules({ isAdmin, canAccessGelPpe, canAccessHygie, canAccessRHnav, isSessionLive, isProprietaireBudget, isHabiliteHestia = false, hestiaAssistanteSeule = false, audit = null, isRhMaitre = false, compteurs = null }) {
  const droits = { canAccessGelPpe, canAccessHygie, canAccessRHnav, isSessionLive, isProprietaireBudget, isHabiliteHestia, hestiaAssistanteSeule, audit, isRhMaitre };
  const menu = modulesDuMenu({ isAdmin, ...droits });
  // Pages réservées aux administrateurs (demande de Pierre du 14/10/2026 : un
  // cadenas ouvert à côté de leur nom, « comme ça je n'hésite plus ») : ce que
  // la garde des pages réserve aux administrateurs (accessScopeForView,
  // src/accesVues.js : themis-, event-, admin…, la page « navrés » des autres)
  // ET ce que le menu montre à un administrateur et non au même compte sans ce
  // droit. Calculé, jamais déclaré à la main (verrou : navConstants.test.js,
  // « cadenas des pages réservées »). Le menu d'un membre n'en porte aucun :
  // un cadenas OUVERT y mentirait.
  return poserCompteurs(isAdmin ? marquerReserveAdmin(menu, modulesDuMenu({ isAdmin: false, ...droits })) : menu, compteurs);
}

/**
 * `reserveAdmin: true` sur un module absent du menu d'un membre (Pilotage), et
 * sur les entrées (accueil, items, enfants) absentes du sien dans un module
 * qu'il voit, ou dont la vue est « admin » pour la garde des pages (Événements :
 * tout sauf la Médiathèque, 14/10/2026). Ne modifie rien en place : les listes
 * d'entrées partagées (RH…) serviraient sinon le cadenas au menu d'un membre.
 */
function marquerReserveAdmin(menu, menuMembre) {
  const vis = (x) => x && x.show !== false;
  const routes = new Map();
  for (const m of menuMembre) {
    if (!vis(m)) continue;
    const r = new Set();
    for (const it of [m.accueil, ...(m.items || [])]) {
      if (!vis(it)) continue;
      r.add(it.route);
      (it.children || []).filter(vis).forEach((c) => r.add(c.route));
    }
    routes.set(m.key, r);
  }
  return menu.map((m) => {
    if (!vis(m)) return m;
    const r = routes.get(m.key);
    if (!r) return { ...m, reserveAdmin: true };
    const marquer = (it) => {
      if (!vis(it)) return it;
      const enfants = Array.isArray(it.children) ? it.children.map(marquer) : it.children;
      const reservee = !r.has(it.route) || accessScopeForView(it.route) === "admin";
      return { ...it, ...(reservee ? { reserveAdmin: true } : {}), ...(enfants ? { children: enfants } : {}) };
    };
    return { ...m, ...(m.accueil ? { accueil: marquer(m.accueil) } : {}), items: (m.items || []).map(marquer) };
  });
}

function modulesDuMenu({ isAdmin, canAccessGelPpe, canAccessHygie, canAccessRHnav, isSessionLive, isProprietaireBudget, isHabiliteHestia, hestiaAssistanteSeule, audit, isRhMaitre }) {
  // Audits (D1) et contrôle interne (D2) : DEUX drapeaux rendus par leurs
  // serveurs (src/audit/drapeauAudit.js, `audit` et `audit.ci`), habilitations
  // « AI » et « CI » séparées depuis le 14/10/2026 ; échec fermé, rien sans eux.
  const drapeauAudit = audit && typeof audit === "object" ? audit : {};
  const drapeauCI = drapeauAudit.ci && typeof drapeauAudit.ci === "object" ? drapeauAudit.ci : {};
  return [
    { key:"athos", label:NAV_LIBELLES.athos, subtitle:"Vie mutualiste", motsCles:"Athos", color:NAV_COLORS.athos, icon:NAV_ICONES.athos, show:true,
      tagline:"Projets, réunions et vie du réseau Enio : tout ce qui fait avancer les mutuelles.",
      accueil:{route:"athos", label:"Accueil Activité", icon:"home"},
      items:[
        // « Demandes d'engagement » (lot L11d-bis, demande de Pierre du 30/09/2026) :
        // en TÊTE du sommaire, pour les seuls administrateurs, avec le nombre de
        // demandes à valider (compteurs de buildNavModules). Venues
        // d'Administration (admin-demandes, qui garde les demandes de compte).
        {route:"athos-demandes", color:NAV_COLORS.athos, label:"Demandes d'engagement", icon:"inbox", description:"Valider les changements d'engagement demandés par les mutuelles.", show:!!isAdmin},
        // « Tableau de bord » (athos-dashboard) : retiré le 30/09/2026 (demande de
        // Pierre, lot L11d). L'engagement du réseau est en tête de la page
        // Projets ; la vue reste valide à jamais (LEGACY_REDIRECTS → accueil).
        // « Solutions » et « Missions » (athos-solutions, athos-missions) : le
        // MÊME écran que la page, sans clé ; depuis le 30/09/2026 un filtre
        // « Catégorie » de la page, les routes redirigent (LEGACY_REDIRECTS).
        // « Missions statutaires » et « Réviser la classification » : dépubliées
        // le 27/08/2026 (décision Pierre), fichiers supprimés au lot L11p ;
        // les routes redirigent vers athos-projets (LEGACY_REDIRECTS).
        {route:"athos-projets", color:"#94C11F", label:"Projets, offres & solutions", icon:"clipboard-list", description:"Suivi des projets, offres et solutions des mutuelles."},
        {route:"athos-reunions", color:"#2AA9E1", label:"Réunions & présences", icon:"calendar", description:"Agenda des réunions et émargements.",
          // « Import Noota » : réservé aux administrateurs (lot L11n) ; la vue
          // garde sa propre garde, son refus est l'écran commun (AccessDenied).
          children:[{route:"athos-noota", label:"Import Noota (transcriptions)", icon:"microphone", color:"#951B81", adminOnly:true, show:!!isAdmin}]},
        {route:"athos-domaines-poles", color:"#F49800", label:"Domaines & Pôles", icon:"category", description:"L'organisation d'Enio par domaine et pôle."},
        {route:"athos-ma-mutuelle", color:"#951B81", label:"Ma mutuelle", icon:"building-community", description:"Le tableau de bord de votre mutuelle."},
      ] },
    // Affaires publiques : un MODULE depuis le 12/10/2026 (décision de Pierre :
    // « Remonte Affaires publiques au même niveau que Activité ou Gouvernance
    // dans le menu latéral, sors-la d'Activité »), juste après Activité, teinte
    // indigo hors charte (NAV_COLORS.ap). Ses entrées sont les anciens enfants
    // de l'entrée « Affaires publiques » d'Activité, dans le même ordre et avec
    // les mêmes droits ; sa page d'accueil reste la vue « affaires-publiques »
    // (AffairesPubliquesView, PLAN_AFFAIRES_PUBLIQUES). Ouvert à TOUS les
    // utilisateurs connectés (demande de Pierre du 02/10/2026 ; lecture
    // vérifiée par le serveur) ; les gestes d'administration vivent DANS les
    // pages, pas au menu.
    // Refonte du 12/10/2026 (maquettes v1 et v2 validées par Pierre) : la page
    // part des usages ; l'ordre du sommaire est celui des maquettes (Textes de
    // loi, le texte en clair, PLF & PLFSS, Parlementaires, Votes et scrutins,
    // Positions d'Enio pour tous, Doctrine et Notes pour l'équipe, Radar,
    // Rapports). « Le texte en clair » est une entrée, pas un enfant de Textes
    // de loi comme dans la maquette v2.
    { key:"ap", label:NAV_LIBELLES.ap, subtitle:"Lois, parlementaires, doctrine", color:NAV_COLORS.ap, icon:NAV_ICONES.ap, show:true,
      tagline:"Suivre les lois et les règlements, connaître les parlementaires et leurs votes.",
      accueil:{route:"affaires-publiques", label:"Accueil Affaires publiques", icon:"home", description:"Suivre les lois et les règlements, connaître les parlementaires et leurs votes."},
      items:[
        // Textes de loi (lot B1.2, ADR-014) : TOUS les dossiers de l'Assemblée
        // (inventaire), ouverts à tous les membres en lecture ; « Suivre » un
        // dossier reste aux administrateurs DANS la page (et au serveur).
        {route:"textes-de-loi", label:"Textes de loi", icon:"file-text", color:NAV_COLORS.ap, description:"Tous les dossiers de l'Assemblée et ceux que suit Enio."},
        // Le texte en clair (maquette v2 du 12/10/2026) : le PLFSS et le PLF
        // 2027 article par article, sourcé, pour tous les membres ; les
        // résumés en langage simple ne sont pas encore écrits (aucune
        // dépense d'IA dans ce chantier) et le menu ne les promet pas ;
        // « Notre position » reste à l'équipe dans la page.
        {route:"texte-en-clair", label:"Le texte en clair", icon:"book", color:NAV_COLORS.ap, description:"Le PLFSS et le PLF 2027 article par article : texte officiel, effet et chiffrage sourcés."},
        // PLF & PLFSS 2027 (commande de Pierre du 04/10/2026) : ouvert à TOUS
        // les membres d'Aramis (décision de Pierre du 05/10/2026) ; réglages,
        // collecte et référence FNMF restent aux administrateurs DANS la
        // page (garde `admin` de src/lf/PlfPlfss.js, et le serveur).
        {route:"plf-plfss-2027", label:"PLF & PLFSS 2027", icon:"report-money", color:NAV_COLORS.ap, description:"La campagne : impacts chiffrés et simulation pour votre mutuelle."},
        // Parlementaires et Votes et scrutins (maquette v1 du 12/10/2026) :
        // deux entrées complètes, valables pour TOUS les textes, ouvertes à
        // tous les membres (lfLire « composition », « parlementaires »,
        // « parlementaire », apScrutinsLire). Aucun score, aucun classement.
        {route:"parlementaires", label:"Parlementaires", icon:"users", color:NAV_COLORS.ap, description:"Composition, groupes et partis, et une fiche par député et par sénateur."},
        {route:"votes-et-scrutins", label:"Votes et scrutins", icon:"users-group", color:NAV_COLORS.ap, description:"Tous les scrutins publics, et ce que chaque groupe a voté."},
        // Veille juridique et réglementaire (décision de Pierre du 18/10/2026) :
        // déplacée de Conformité, ouverte à tous les membres connectés ; au
        // rail, la teinte du module (verrou « Affaires publiques est un
        // module ») ; la note de veille réglementaire porte le violet.
        {route:"veille-juridique", label:"Veille juridique et réglementaire", icon:"gavel", color:NAV_COLORS.ap, description:"Textes et décisions des autorités (ACPR, CNIL, EIOPA…), Journal officiel et JOUE."},
        // Positions d'Enio (doctrine ouverte, lot L2, conception validée par
        // Pierre le 12/10/2026) : la synthèse de chaque thème publié, ouverte
        // à TOUS les membres connectés (ni adminOnly ni show conditionnel) ;
        // un compte invité ne la lit pas (App.jsx, et le serveur :
        // doctrineMembres, jeton Aramis de la session réelle).
        {route:"doctrine-positions", label:"Positions d'Enio", icon:"flag", color:NAV_COLORS.ap, description:"Ce que défend Enio, thème par thème, tel qu'il a été arrêté."},
        // Doctrine d'Enio (décisions de Pierre du 11/10/2026) : l'atelier où
        // la doctrine est arrêtée thème par thème par les valideurs des
        // notes, depuis un premier jet importé. Visible de l'équipe d'Enio
        // seule (administrateurs ; le serveur le revérifie, jeton Aramis de
        // la session réelle, passkey exigée) ; les membres en lisent les
        // synthèses publiées dans « Positions d'Enio ».
        {route:"doctrine", label:"Doctrine d'Enio", icon:"scale", color:NAV_COLORS.ap, adminOnly:true, show:!!isAdmin, description:"Les énoncés arrêtés et les amendements lus à leur regard."},
        // Notes institutionnelles (B6, 11/10/2026) : modèles de note, dossier
        // de sources, versions, relecture et validation obligatoires, export
        // Word. Réservées aux administrateurs (le serveur le revérifie, jeton
        // Aramis de la session réelle) ; aucune lecture par les membres.
        {route:"notes-institutionnelles", label:"Notes institutionnelles", icon:"notebook", color:NAV_COLORS.ap, adminOnly:true, show:!!isAdmin, description:"Préparer, relire, valider, exporter en Word."},
        // Notes d'Enio reçues (lot L4, décision de Pierre du 12/10/2026) :
        // les notes validées diffusées à la personne connectée, lues par
        // leurs seuls destinataires (et l'équipe) ; ouvert à tous les
        // membres connectés (le serveur, notesRecues, ne sert que les
        // siennes ; un compte invité ne lit rien). Rangée après les notes
        // de l'équipe à la refonte du 12/10/2026.
        {route:"notes-recues", label:"Notes d'Enio reçues", icon:"mail-down", color:NAV_COLORS.ap, description:"Les notes d'Enio qui vous ont été diffusées."},
        {route:"radar-presidentielle-2027", label:"Radar Présidentielle 2027", icon:"radar", color:NAV_COLORS.ap, description:"Sondages, second tour, programmes au prisme mutualiste."},
        // Rapports AMO-AMC (demande de Pierre du 05/10/2026) : les deux rapports
        // publics de septembre 2026 (personnalités qualifiées ; chiffrage
        // IGAS-IGF) et le communiqué de la Mutualité Française ; ouvert à tous
        // les membres d'Aramis le 05/10/2026 (pièces publiques, aucune donnée
        // serveur lue).
        {route:"rapports-amo-amc", label:"Rapports AMO-AMC", icon:"books", color:NAV_COLORS.ap, description:"Les deux rapports de septembre 2026 et la réponse de la Mutualité."},
      ] },
    { key:"gouv", label:NAV_LIBELLES.gouv, subtitle:"Instances, votes et consultations", motsCles:"Thémis Agora", color:NAV_COLORS.gouv, icon:NAV_ICONES.gouv, show:true,
      tagline:"Instances, votes et consultations : la vie démocratique de l'Union.",
      accueil:{route:"accueil-gouv", label:"Accueil Gouvernance", icon:"home"},
      items:[
        // Une entrée PAR INSTANCE (décision Pierre 04/09/2026) : la page
        // s'ouvre sur ses séances et leurs documents ; ses rubriques sont les
        // enfants du rail. Source unique : src/instancesNav.js.
        ...INSTANCES_NAV.map(i => ({
          route:i.route, color:i.couleur, instance:i.key, label:i.label, icon:i.icon, description:i.description,
          children: sectionsPour(i).filter(s => s.key!=="seances")
            .map(s => ({ route:routeInstance(i.key, s.key), label:s.label, icon:s.icon, color:i.couleur })),
        })),
        {route:"participant", color:"#D60B52", label:"Voter", icon:"checkbox", show:isSessionLive, description:"Voter pendant une séance en cours."},
        {route:"results", color:"#1D948A", label:"Résultats en direct", icon:"chart-bar", show:isSessionLive, description:"Les résultats des scrutins en direct."},
        // Écran de salle : retiré du menu Gouvernance (décision de Pierre du
        // 18/10/2026) ; la vue « screen » reste valide et s'ouvre depuis la
        // console de séance (bouton « Écran de salle »), réservée à l'équipe.
        {route:"consultations", color:"#F49800", label:"Consultations", icon:"clipboard-check", description:"Consultations et sondages des membres."},
        // Transverses (Mandats & mandatures, Organigramme, Documents communs,
        // Vue d'ensemble) : cartes discrètes de l'Accueil Gouvernance, pas
        // d'entrée de menu (lot 3, décision Pierre 04/09). Routes conservées
        // dans MODULE_VIEWS.gouv via GOUVERNANCE_VIEWS.
        // « Enio 2030 » (strategie-2030) : retiré du menu le 30/09/2026, puis
        // la consultation elle-même le 01/10/2026 (décision Pierre). La vue
        // reste valide (liens des courriels, favoris) et redirige vers
        // l'accueil Gouvernance (LEGACY_REDIRECTS), gardée dans MODULE_VIEWS.gouv.
        // « Hestia » prend sa place (R2, décision du DG d'Enio du 01/10/2026) : un
        // nouveau départ, qui ne la cite pas. Entrée des seules personnes
        // habilitées (drapeau chargé APRÈS le premier rendu, src/hestia/
        // drapeauHestia.js) ; la vue reste gardée côté serveur. Teinte de
        // Gouvernance hors d'Hestia (A1).
        {route:"hestia", color:NAV_COLORS.gouv, label:"Hestia", icon:"compass", show:!!isHabiliteHestia,
          description:"L'espace de travail du projet stratégique de l'Union : les sept objectifs adoptés par le conseil d'administration.",
          // « À traiter » : rien n'y attend une assistante, qui lit seulement.
          children:hestiaAssistanteSeule ? [] : [{route:"hestia-a-traiter", label:"À traiter", icon:"inbox", color:NAV_COLORS.gouv}]},
        {route:"ideas", color:"#F49800", label:"Boîte à idées", icon:"bulb", description:"La boîte à idées collaborative."},
        {route:"questions-historique", color:"#2AA9E1", label:"Questions & entraide", icon:"message-question", description:"Questions, entraide et réponses entre membres."},
        {route:"travail-groupes", color:"#1D948A", label:"Travail en groupes", icon:"users-group", description:"Les espaces de travail en groupes."},
      ] },
    { key:"mutuelles", label:NAV_LIBELLES.mutuelles, subtitle:"Le réseau Enio", color:NAV_COLORS.mutuelles, icon:NAV_ICONES.mutuelles, show:true,
      tagline:"Le réseau Enio : qui en fait partie, où, et ses chiffres clés.",
      accueil:null,
      items:[
        {route:"athos-annuaire", color:"#F49800", label:"Annuaire des membres d'Enio", icon:"address-book", description:"Coordonnées et délégués de chaque mutuelle membre."},
        {route:"athos-implantations", color:"#1D948A", label:"Panorama territorial", icon:"map", description:"La carte des implantations du réseau."},
        {route:"dataviz", color:"#2AA9E1", label:"Panorama économique", icon:"chart-line", description:"Les indicateurs économiques consolidés des mutuelles."},
        // Les mutuelles en clair (commande de Pierre du 11/10/2026) : les SFCR
        // publics lus par l'IA, vérifiés par le serveur ; ouverte à tous les
        // membres (fiches publiées seulement), administration dans la page.
        {route:"mutuelles-en-clair", color:NAV_COLORS.mutuelles, label:"Les mutuelles en clair", icon:"eye", description:"Ce que sont et font les mutuelles, lu dans leurs rapports publics de solvabilité depuis 2022."},
        // Fondations et fonds de dotation des mutuelles (demande de Pierre du
        // 18/10/2026) : relevés de Cowork publiés par un administrateur,
        // synthèses aux mutuelles ; ouverte à tous les membres.
        {route:"fondations-mutuelles", color:NAV_COLORS.mutuelles, label:"Fondations et fonds de dotation", icon:"sparkles", description:"Appels à projets, projets soutenus, comités et actions des fondations et fonds de dotation des mutuelles."},
        {route:"referentiel-organismes", color:"#003F59", label:"Registre des organismes d'assurance", icon:"list-details", description:"Le registre officiel ACPR des organismes d'assurance."},
        {route:"nouvelles-mutuelles", color:"#D60B52", label:"Nouvelles des mutuelles", icon:"news", description:"Les actualités et nouvelles du réseau."},
      ] },
    // Argos — intelligence économique. Module transverse : il croise le registre
    // ACPR (Nos Mutuelles), les attributions BOAMP (Marchés) et les listes de
    // gel/PPE (Conformité), d'où son rang de premier niveau plutôt qu'une entrée
    // rangée sous l'un des trois. La home du module EST la recherche : pas de
    // ModuleVitrine, car chaque clic intercalé tuerait l'adoption d'un outil qui
    // remplace un signet externe.
    { key:"argos", label:NAV_LIBELLES.argos, subtitle:"Intelligence économique", motsCles:"Argos", color:NAV_COLORS.argos, icon:NAV_ICONES.argos, show:true,
      tagline:"Tout savoir sur une entreprise ou une association : identité, dirigeants, détention, comptes, annonces, marchés publics.",
      accueil:{route:"argos", label:"Rechercher une entreprise", icon:"search"},
      items:[
        {route:"argos-surveillance", color:"#CBBB9F", label:"Entités sous surveillance", icon:"eye", description:"Les entreprises que vous suivez et leurs mouvements : annonces légales, dirigeants, procédures."},
        {route:"argos-dossiers", color:"#F49800", label:"Mes dossiers", icon:"folder", description:"Vos dossiers d'analyse et leurs exports PDF datés et sourcés."},
        {route:"argos-sources", color:"#1D948A", label:"Sources & fraîcheur", icon:"database", description:"D'où viennent les données affichées et à quelle date elles ont été collectées."},
        {route:"argos-journal", color:"#003F59", label:"Mes consultations", icon:"history", description:"Les recherches et fiches que vous avez consultées, et la durée de conservation de cette trace."},
      ] },
    { key:"exploration", label:NAV_LIBELLES.exploration, subtitle:"Commande publique", color:NAV_COLORS.exploration, icon:NAV_ICONES.exploration, show:true,
      tagline:"Détecter et suivre les appels d'offres publics du secteur.",
      accueil:null,
      items:[
        {route:"veille", color:"#2AA9E1", label:"Veille appels d'offres publics", icon:"file-search", description:"Les appels d'offres publics (BOAMP), filtres et alertes."},
      ] },
    { key:"events", label:NAV_LIBELLES.events, subtitle:"Affaires extérieures", color:NAV_COLORS.events, icon:NAV_ICONES.events, show:true,
      tagline:"Événements externes de bout en bout : invitations, RSVP, plan de table, jour J, bilan.",
      // Porte d'un membre (lot L17.2a, relecture du 04/10) : l'accueil des
      // Événements lui est fermé (vitrine) ; la Médiathèque est la page du
      // module qui lui est ouverte. Rail, feuille « Modules », onglet du
      // téléphone, palette et ⌥7 (septième module depuis le 12/10/2026) l'y
      // mènent directement (routePortail).
      ...(isAdmin ? {} : { porte:"mediatheque" }),
      accueil:{route:"themis-evenements", label:"Événements", icon:"calendar-event",
        children:[
          {route:"themis-events-list", label:"Tous les événements", icon:"list-details", color:"#D60B52"},
          {route:"themis-evenements-validation", label:"Centre de validation", icon:"circle-check", color:"#F49800"},
          {route:"event-suivi", label:"Suivi & alertes", icon:"bell", color:"#951B81"},
        ]},
      items:[
        // Médiathèque (lot L17.2a) : photos des événements, des séances et
        // albums libres, affichables, jamais téléchargeables (Pierre, 04/10).
        // `ouverteAuxMembres` : la vitrine d'une page fermée du module la rend
        // cliquable (VitrineAcces.js), au lieu d'un index sans lien.
        {route:"mediatheque", color:NAV_COLORS.events, label:"Médiathèque", icon:"library-photo", description:"Les photos des événements, des séances et des albums libres, à regarder dans Aramis.", ouverteAuxMembres:true},
        {route:"event-day", color:"#951B81", label:"Accueil jour J (tablette)", icon:"ticket", description:"Émargement, scan QR et walk-ins : l'écran de l'hôtesse le jour de l'événement."},
        {route:"themis-handwritten", color:"#003F59", label:"Manuscrits", icon:"signature", description:"Bibliothèque d'annotations manuscrites (appels, clôtures, signatures) pour les invitations."},
        {route:"themis-contacts", color:"#D60B52", label:"Contacts externes", icon:"address-book", description:"Le carnet d'invités : partenaires, élus, acteurs du secteur, catégories et VIP."},
      ] },
    { key:"rh", label:NAV_LIBELLES.rh, subtitle:"Recrutement · Formation · SIRH", color:NAV_COLORS.rh, icon:NAV_ICONES.rh, show:true,
      tagline:"Recrutement, personnel, formation et GPEC : les ressources humaines mutualisées du réseau Enio.",
      accueil:{route:"rh", label:"Accueil Ressources humaines", icon:"home"},
      // `adminOnly` : administrateurs ; `rhMaitre` : RH Maître ou administrateur,
      // le droit que RHModule lit de rhWhoAmI et note pour le rail (src/droitRH.js).
      items: RH_NAV_ITEMS.map(it => {
        const droit = (x) => x.adminOnly ? { ...x, show: !!isAdmin } : x.rhMaitre ? { ...x, show: !!(isAdmin || isRhMaitre) } : x;
        const e = droit(it);
        return e.children ? { ...e, children: e.children.map(droit) } : e;
      }) },
    { key:"hygie", label:NAV_LIBELLES.hygie, subtitle:"RGPD, veille juridique, LCB-FT", motsCles:"Calypso Hygie", color:NAV_COLORS.hygie, icon:NAV_ICONES.hygie, show:true,
      tagline:"Conformité : RGPD, veille réglementaire et lutte anti-blanchiment.",
      accueil:null,
      items:[
        {route:"hygie", color:"#E20816", label:"RGPD (Calypso)", icon:"shield", description:"Registre RGPD, DPO et conformité des données (Calypso).",
          // Sous-menu Calypso (demande Pierre 22/08/2026) : chaque onglet du
          // module est une vue autonome (DPOGate) → navigable depuis la barre.
          children:[
            {route:"hygie-dashboard", label:"Tableau de bord conformité", icon:"layout-dashboard", color:"#003F59"},
            {route:"hygie-registre", label:"Registre des traitements", icon:"list-details", color:"#E20816"},
            {route:"hygie-aipd", label:"AIPD", icon:"target", color:"#951B81"},
            {route:"hygie-dsar", label:"DSAR : demandes des personnes", icon:"inbox", color:"#2AA9E1"},
            {route:"hygie-violations", label:"Violations de données", icon:"alert-triangle", color:"#F49800"},
            {route:"hygie-subprocessors", label:"Sous-traitants", icon:"heart-handshake", color:"#1D948A"},
            {route:"hygie-formation", label:"Formation & sensibilisation", icon:"school", color:"#94C11F"},
            {route:"hygie-library", label:"Bibliothèque mutualisée", icon:"books", color:"#2AA9E1"},
            {route:"hygie-forum", label:"Espace d'échange entre DPO", icon:"messages", color:"#F49800"},
            {route:"hygie-annuaire", label:"Annuaire & organisme", icon:"address-book", color:"#2AA9E1"},
            {route:"hygie-library-admin", label:"Bibliothèque (administration)", icon:"settings", color:"#003F59", show:!!isAdmin},
          ]},
        // Audits (D1, 11/10/2026), sans lien avec Calypso : grilles et plans
        // d'audit, ÉTANCHES entre les mutuelles. Visibles des fiches DG d'une
        // mutuelle, des fiches habilitées « AI » (14/10/2026) et du
        // super-administrateur d'Enio, d'après le serveur (drapeauAudit) ; un
        // administrateur ordinaire n'y a pas accès. La fiche d'une mission (audit-mission)
        // s'ouvre depuis la liste.
        {route:"audit", color:NAV_COLORS.hygie, label:"Audit interne", icon:"checklist", show:drapeauAudit.habilite === true,
          description:"Audit interne : les grilles et plans d'audit de votre mutuelle, avec points de contrôle, constats, actions, preuves et clôture.",
          children:[
            // Le plan d'audit de la mutuelle, créé par elle (13/10/2026) ; la
            // fiche d'un plan (audit-plan) s'ouvre depuis la liste.
            {route:"audit-plans", label:"Plan d'audit", icon:"calendar-event", color:NAV_COLORS.hygie},
            // Le registre des constats, recommandations et actions de la
            // mutuelle (13/10/2026) ; la fiche d'un constat (audit-constat)
            // s'ouvre depuis le registre.
            {route:"audit-registre", label:"Suivi des recommandations", icon:"clipboard-check", color:NAV_COLORS.hygie},
            {route:"audit-actions", label:"Actions en cours", icon:"flag", color:NAV_COLORS.hygie},
            {route:"audit-modeles", label:"Modèles de grilles", icon:"template", color:NAV_COLORS.hygie, show:drapeauAudit.modeles === true},
            // L'accès en DERNIER, dans chaque module (demande de Pierre du 15/10/2026).
            {route:"audit-acces", label:"Accès à l'audit interne", icon:"key", color:NAV_COLORS.hygie, show:drapeauAudit.gererAcces === true},
          ]},
        // Contrôle interne (D2, 11/10/2026), sans lien avec Calypso : la
        // cartographie des risques et les procédures, ÉTANCHES entre les
        // mutuelles, ouvertes au DG et aux fiches habilitées « CI » par SON
        // drapeau (drapeauCI, 14/10/2026), jamais par celui de l'audit. La fiche d'un risque
        // (risque) et celle d'une procédure (procedure) s'ouvrent depuis leurs
        // listes ; le référentiel commun est tenu par le super-administrateur.
        {route:"controle-interne", color:NAV_COLORS.hygie, label:"Contrôle interne", icon:"target", show:drapeauCI.habilite === true,
          description:"La cartographie des risques et les procédures de votre mutuelle : maîtrises, preuves, évaluations, versions approuvées.",
          children:[
            {route:"risques", label:"Cartographie des risques", icon:"layout-grid", color:NAV_COLORS.hygie},
            {route:"risques-tableau", label:"Tableau des risques", icon:"list-details", color:NAV_COLORS.hygie},
            // Échange de la Conformité avec Cowork (12/10/2026) : la
            // bibliothèque commune des risques types (choisir, coter). Rien
            // n'est proposé à une mutuelle donnée (décision de Pierre du
            // 13/10/2026). L'échange lui-même (état, réglages,
            // « Synchroniser ») vit dans Super administration › Synchronisation
            // depuis le 13/10/2026 ; les propositions communes de Cowork
            // (conformite-propositions) s'ouvrent depuis le contrôle interne.
            {route:"risques-bibliotheque", label:"Bibliothèque des risques", icon:"books", color:NAV_COLORS.hygie},
            {route:"procedures", label:"Procédures", icon:"notebook", color:NAV_COLORS.hygie},
            {route:"controle-interne-referentiel", label:"Référentiel du contrôle interne", icon:"settings", color:NAV_COLORS.hygie, show:drapeauCI.referentiel === true},
            // L'accès en DERNIER (demande de Pierre du 15/10/2026).
            {route:"controle-interne-acces", label:"Accès au contrôle interne", icon:"key", color:NAV_COLORS.hygie, show:drapeauCI.gererAcces === true},
          ]},
        // La « Mutuelle de démonstration » (13/10/2026, décision de Pierre) :
        // tout membre connecté la lit (données fictives, mise en garde sur
        // chaque page), les administrateurs d'Enio la modifient. Ses pages
        // sont celles du contrôle interne et de l'audit interne, en ?demo=1.
        {route:"conformite-demonstration", color:NAV_COLORS.hygie, label:"Démonstration", icon:"presentation",
          description:"La Mutuelle de démonstration : ce que donnent le contrôle interne et l'audit interne après trois ans d'usage, sur des données fictives."},
        // « Paramétrage de ma mutuelle » (lot 2 de la recette du 16/10/2026) :
        // échelle à 4 ou 5, types d'impact et règle de la cotation, réglés par
        // le DG de la mutuelle ; DERNIÈRE entrée du menu Conformité (décision
        // de Pierre du 17/10/2026). Le lot 8 y ajoutera ses réglages.
        {route:"parametrage-mutuelle", color:NAV_COLORS.hygie, label:"Paramétrage de ma mutuelle", icon:"building-community",
          show:drapeauCI.gererAcces === true || drapeauCI.referentiel === true,
          description:"Ce que votre mutuelle règle elle-même : l'échelle de cotation des risques (4 ou 5 degrés), les types d'impact et la règle de calcul."},
        // « Gel des avoirs et PPE » (gel-avoirs-ppe) : retiré du menu le
        // 30/09/2026 (décision Pierre). La vue reste valide (alertes par
        // courriel, favoris) : gardée dans MODULE_VIEWS.hygie.
      ] },
    // Pilotage — outils INTERNES Enio, réservés aux administrateurs : la
    // préparation des séances des instances (depuis le 04/09/2026, décision
    // Pierre — l'entrée « Instances » du menu Gouvernance égarait les membres
    // sur une page réservée) et les outils financiers.
    // `equipe` : module de l'équipe d'Enio (administrateurs). Au doigt, la
    // feuille « Modules » le range avec Administration et Super administration,
    // hors de la grille des modules (12/10/2026 : dix modules ne tiennent pas en
    // un rectangle plein, voir FeuilleModules.js). Le rail, la palette et
    // ⌥1 à ⌥0 le gardent parmi les modules, à sa place.
    { key:"pilotage", label:NAV_LIBELLES.pilotage, subtitle:"Séances · Budget · Dépenses", color:NAV_COLORS.pilotage, icon:NAV_ICONES.pilotage, show:isAdmin, equipe:true,
      tagline:"Le pilotage interne d'Enio : préparation des séances, budget, cotisations, dépenses.",
      accueil:{route:"accueil-pilotage", label:"Accueil Pilotage", icon:"home"},
      items:[
        {route:"pilotage-seances", color:"#951B81", label:"Séances des instances : préparation", icon:"gavel", description:"Préparer, convoquer, tenir et clore les CA, AG, CODIR et Bureau."},
        // « Émargement & codes de vote » et « Scrutins & votes en séance » :
        // deux ÉTAPES du rail de la fiche de séance (demande de Pierre du
        // 18/10/2026, qui remplace leurs entrées ici du même jour). Les vues
        // seance-emargement et seance-scrutins restent valides à jamais.
        // Vote en direct de l'AG (24/09/2026) : console des admins — propositions,
        // deux tours, révélation sur l'écran de salle. Les pages publiques
        // (/vote/<code>, /vote/<code>/ecran) sont montées hors App (src/index.js).
        {route:"admin-vote-ag", color:"#F49800", label:"Vote en direct (AG)", icon:"checkbox", description:"Propositions, deux tours de vote et révélation en direct sur l'écran de la salle."},
        {route:"admin-budget", label:"Budget & cotisations", icon:"coins", description:"Gestion du budget et des cotisations des mutuelles."},
        // `show` suit le VRAI droit d'accès. Avec `!!isAdmin`, tous les admins
        // voyaient une entrée qui ne leur affichait qu'un cadenas.
        // L'entrée PARENTE mène à « où l'on en est », pas à la simulation :
        // cliquer « Budget » pour tomber sur un simulateur d'exercice futur
        // n'est ni logique ni trouvable. La simulation devient un enfant, nommé.
        // Page « menu » du Budget (07/10/2026, demande de Pierre) : « Budget »
        // mène à son accueil, qui liste ses pages comme les accueils de module ;
        // la consommation de l'exercice devient « Consommation », une page parmi
        // les autres. BUDGET_PAGES, source unique du sommaire et de l'accueil.
        {route:"budget-accueil", color:"#1D948A", label:"Budget", icon:"wallet", description:"Consommation de l'exercice, simulation, trésorerie, dépenses, vision par compte et paie.", show:!!isProprietaireBudget,
          children: BUDGET_PAGES},
        {route:"admin-depenses", label:"Dépenses & paiements", icon:"receipt", description:"Factures fournisseurs, validation et virements SEPA groupés."},
        {route:"admin-ndf", color:"#94C11F", label:"Notes de frais", icon:"luggage", description:"Contrôle, validation et remboursement des notes de frais ; profils, circuits et plafonds."},
        // Pas d'entrée « Comptabilité générale » : le module (ComptaModule.js)
        // est livré au dépôt mais DORMANT — aucune entrée de menu, aucune route
        // dans App.jsx (décision Pierre 01/08/2026, nous ne nous en servons pas
        // à ce stade). Ne pas le rajouter ici sans accord explicite : cette
        // liste alimente à la fois la barre desktop et le drawer mobile.
        {route:"compta-ec", color:"#951B81", label:"Comptabilité : exports", icon:"calculator", description:"Envoi mensuel de l'export au cabinet comptable, pièces et imputation, exports ACD et FEC."},
        {route:"preparer-presentation", color:"#951B81", label:"Préparer une présentation", icon:"presentation", description:"Générer une présentation au format Enio."},
        {route:"historiques", color:"#003F59", label:"Historiques des séances & scrutins", icon:"archive", description:"L'historique des séances et scrutins."},
      ] },
  ];
}

// Anciennes strings de vue → destination actuelle. Les clés restent des vues
// VALIDES (App.jsx les rend par LegacyViewRedirect) mais n'apparaissent dans
// aucun menu — src/navConstants.test.js le vérifie.
/**
 * Les écrans du module Budget. SOURCE UNIQUE : App.jsx l'importe pour savoir
 * quoi monter, et BudgetToolboxPanel pour savoir quoi titrer.
 *
 * La liste vivait recopiée dans App.jsx. En ajoutant « budget-exercice » au
 * menu sans l'ajouter là, on a livré une entrée de menu qui menait à une page
 * BLANCHE : la barre latérale s'affichait, et rien ne se montait. Un test
 * vérifie désormais que tout enfant du menu Budget figure ici.
 */
export const VUES_BUDGET = [
  "budget-accueil",
  "budget-simulation", "budget-exercice", "budget-situation",
  "budget-depenses", "budget-compte", "budget-paie",
];

export const LEGACY_REDIRECTS = {
  "themis-instances": "pilotage-seances",
  "themis-preparer": "pilotage-seances",
  "themis": "pilotage-seances",          // membre non-admin → accueil-gouv (App.jsx)
  "agora": "accueil-gouv",               // ancien accueil Agora → accueil Gouvernance
  "admin-budget-outils": "budget-accueil",      // boîte à outils éclatée en écrans (09/09/2026), accueil du Budget depuis le 07/10/2026
  "classement": "mon-profil",            // points et classements retirés (lot L1a, 27/09/2026) ; carnet de mandat à venir
  "athos-dashboard": "athos",            // Tableau de bord Activité retiré (lot L11d, 30/09/2026) → accueil Activité
  // Cartographie et plan d'audit PROPOSÉS à une mutuelle par Cowork : retirés
  // le 13/10/2026 (décision de Pierre : rien n'est proposé à une mutuelle sur
  // ses documents publics ; sa cartographie et son plan sont les siens).
  "risques-propositions": "risques",
  "audit-propositions": "audit",
  // « Échange avec Cowork » de la Conformité : sorti du rail le 13/10/2026
  // (demande de Pierre : tous les échanges avec Cowork sur une page) ; ses
  // propositions communes restent au contrôle interne (conformite-propositions).
  "conformite-cowork": "super-synchronisation",
  "ressources": "athos",                 // « Ressources documentaires », page supprimée de longue date, encore cible de liens de relance (11/10/2026) → accueil Activité
  // Lot L11p (30/09/2026) : vues mortes redirigées, jamais de page blanche.
  "accueil": "ma-semaine",               // ancienne page d'accueil (connecté seulement : hors connexion, la garde de session répond)
  "athos-home-classique": "accueil-athos",   // ancienne home Activité, gardée en rollback depuis 05/2026
  "athos-migration-review": "athos-projets",       // « Réviser la classification », dépubliée le 27/08/2026
  "athos-missions-statutaires": "athos-projets",   // « Missions statutaires », dépubliée le 27/08/2026
  // Alias de la page Projets, même écran sans clé (30/09/2026) : un lien de
  // projet ne s'y ouvrait pas. La catégorie voyage en paramètre ({ categorie }).
  "athos-solutions": "athos-projets",
  "athos-missions": "athos-projets",
  "athos-projets-classique": "athos-projets",
  "questions": "questions-historique",   // aucune page n'a jamais porté ce nom seul
  "argos-graphe": "argos",               // page annoncée, jamais livrée ; siren et q passés conservés
  // Consultation « Enio 2030 » retirée le 01/10/2026 (G11, « Plus personne, on
  // supprime »). Les courriels en circulation (invitations, relances, digest,
  // mentions) portent ?view=strategie-2030 sans paramètre : ils mènent à
  // l'accueil Gouvernance, À JAMAIS (jamais vers Hestia, qui ne la cite pas).
  "strategie-2030": "accueil-gouv",
  // Veille réglementaire RGPD de Calypso : retirée le 21/10/2026 (décision de
  // Pierre : « elle est dans Veille juridique et réglementaire des Affaires
  // publiques »). La redirection présélectionne la famille « CNIL, DORA &
  // cybersécurité » (CNIL et CEPD), App.jsx.
  "hygie-veille": "veille-juridique",
};

// ── COQUILLE (lot L6 « Coquille et transitions », 28/09/2026) ────────────────
// Le rail, la feuille « Modules », la palette et les raccourcis ⌥1 à ⌥9 (et ⌥0
// pour le dixième) ouvrent un module par sa PORTE : la route que prenaient les
// anciens boutons de l'en-tête. Reprise telle quelle (Activité ouvre sa
// vitrine, Entreprises sa recherche, Événements son accueil, RH son accueil) :
// aucun lien ne change de destination. Affaires publiques (module depuis le
// 12/10/2026) ouvre sa page, l'ancienne entrée d'Activité. Chaque porte figure
// dans MODULE_VIEWS de son module (src/navConstants.test.js).
export const PORTAILS_MODULES = {
  athos: "accueil-athos", ap: "affaires-publiques", gouv: "accueil-gouv", mutuelles: "accueil-mutuelles",
  exploration: "accueil-marches", argos: "argos", events: "themis-evenements",
  hygie: "accueil-conformite", rh: "rh", pilotage: "accueil-pilotage",
};
// `m.porte` (posée par modulesDuMenu selon la session) passe devant : la porte
// d'un membre aux Événements est la Médiathèque (lot L17.2a). Elle figure
// elle aussi dans MODULE_VIEWS de son module (src/coquilleNavigation.test.js).
export const routePortail = (m) =>
  (m && (m.porte || PORTAILS_MODULES[m.key] || (m.accueil && m.accueil.route) || (m.items && m.items[0] && m.items[0].route))) || "ma-semaine";

/** Libellé court d'une entrée d'accueil (« Accueil Activité » → « Accueil »). */
export const libelleEntree = (it) => (it && /^Accueil\b/i.test(it.label || "") ? "Accueil" : (it && it.label) || "");

/** Entrées d'un module À PLAT, dans l'ordre du menu : accueil, puis chaque
 *  entrée suivie de ses enfants. `show:false` exclu. `parent` = route du parent. */
export function entreesModule(m) {
  if (!m) return [];
  const out = [];
  const vis = (x) => x && x.show !== false;
  if (vis(m.accueil)) {
    out.push({ ...m.accueil, parent: null, niveau: 1 });
    (m.accueil.children || []).filter(vis).forEach((c) => out.push({ ...c, parent: m.accueil.route, niveau: 2 }));
  }
  (m.items || []).filter(vis).forEach((it) => {
    out.push({ ...it, parent: null, niveau: 1 });
    (it.children || []).filter(vis).forEach((c) => out.push({ ...c, parent: it.route, niveau: 2 }));
  });
  return out;
}

// Écrans personnels, hors module : leur nom dans le fil courant et l'en-tête.
export const LIBELLES_PERSONNELS = {
  "ma-semaine": "Ma semaine", "mon-profil": "Mon profil", "classement": "Mon profil",
  "mes-notes-frais": "Mes notes de frais", "mon-espace-salarie": "Mon espace salarié", "mes-formations": "Mes formations",
  "tour-hub": "Visites guidées",
};

/**
 * Fil courant d'une vue (« Enio / Gouvernance / Conseil d'administration ») :
 * la liste des étapes APRÈS « Enio ». Chaque étape : { libelle, route? }.
 * Pur, testé (src/coquilleNavigation.test.js). `modules` = buildNavModules(…).
 */
export function filCourant(view, modules, { admin = false } = {}) {
  if (!view) return [];
  if (LIBELLES_PERSONNELS[view]) return [{ libelle: LIBELLES_PERSONNELS[view], route: view === "classement" ? "mon-profil" : view }];
  // Un espace de Mon espace salarié : « Enio / Mon espace salarié / Mes entretiens ».
  const espace = NAV_ESPACE_SALARIE_ITEMS.find((it) => it.route === view);
  if (espace) return [{ libelle: LIBELLE_ESPACE_SALARIE, route: ACCUEIL_ESPACE_SALARIE.route }, { libelle: espace.label, route: espace.route }];
  for (const k of ["admin", "superadmin"]) {
    if (!MODULE_VIEWS[k].includes(view)) continue;
    const it = ITEMS_ADMIN[k].find((x) => x.route === pageServie(view));
    // Toute page de l'Administration est réservée : le cadenas (14/10/2026).
    return [{ libelle: LIBELLES_ADMIN[k], ...(it ? {} : { reserve: true }) }, ...(it ? [{ libelle: it.label, route: it.route, reserve: true }] : [])];
  }
  for (const m of modules || []) {
    if (!m || m.show === false) continue;
    const entrees = entreesModule(m);
    const e = entrees.find((x) => x.route === pageServie(view));
    const dedans = e || (MODULE_VIEWS[m.key] || []).includes(view);
    if (!dedans) continue;
    const fil = [{ libelle: m.label, route: routePortail(m) }];
    let reserve = !!m.reserveAdmin;
    if (e) {
      if (e.parent) {
        const p = entrees.find((x) => x.route === e.parent);
        if (p) fil.push({ libelle: libelleEntree(p), route: p.route });
        if (p && p.reserveAdmin) reserve = true;
      }
      if (e.reserveAdmin) reserve = true;
      if (e.route !== routePortail(m) || e.parent) fil.push({ libelle: libelleEntree(e), route: e.route });
    }
    // Le nom de la page (dernier élément) porte le cadenas d'une page réservée
    // aux administrateurs (buildNavModules, 14/10/2026) ; pour un
    // administrateur, aussi une sous-page que la garde réserve (fiche d'un
    // événement…) sans entrée qui la serve.
    if (admin && accessScopeForView(view) === "admin") reserve = true;
    if (reserve) fil[fil.length - 1] = { ...fil[fil.length - 1], reserve: true };
    return fil;
  }
  return [];
}
